import React, { useState, useEffect } from 'react';
import { Button, Modal, Select, Input, message, Dropdown, Progress, Typography, Row, Col, Checkbox } from 'antd';
import ExportFieldsModal from './ExportFieldsModal';
import { DeleteOutlined, InboxOutlined, PrinterOutlined, CloseCircleOutlined, ExportOutlined, UserSwitchOutlined, SettingOutlined, FilePdfOutlined, SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getAuthHeader } from '../../utils/auth';
import { getCustomers } from '../../services/userService';
import { batchUpdateOrderFlag } from '../../services/airwayBillService';
import { exportOrders } from '../../services/orderService';

const { Text } = Typography;

const OrderListToolbar = ({ 
  selectedOrderIds, 
  isSearchMode, 
  onDelete,
  onBatch, 
  onClearSearch,
  onPrint,
  totalOrders,
  onChangeCustomer,
  onOrdersUpdate,
  orders = [],
  onSearchResults,
  onDownloadPdf,
  isMyOrdersRoute = false,
  isOrderManagementTab = false,
  // Search functions
  onSearchOrderDirect,
  onSearchExpressDirect,
  onSearchBatchDirect,
  onSearchRecipientDirect,
  onSearchRecipientPhoneDirect,
  onSearchSenderDirect,
  onSearchSenderPhoneDirect,
  onSearchStatusDirect,
  onSearchPrintCountDirect,
  onSearchDateFilterDirect
}) => {
  const [isCustomerModalVisible, setIsCustomerModalVisible] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [totalOrdersToUpdate, setTotalOrdersToUpdate] = useState(0);
  const [updatedOrders, setUpdatedOrders] = useState(0);
  const [isFilteringUncollected, setIsFilteringUncollected] = useState(false);
  const [originalOrders, setOriginalOrders] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUserRole(payload.role);

      if (['BOSS', 'MANAGER', 'STAFF'].includes(payload.role)) {
        const fetchCustomers = async () => {
          try {
            const data = await getCustomers();
            setCustomers(data);
          } catch (error) {
            message.error('获取客户列表失败');
          }
        };
        fetchCustomers();
      }
    }
  }, []);
  
  // // Auto-update order statuses when orders are loaded
  // useEffect(() => {
  //   // Only trigger auto-update if:
  //   // 1. We have orders to update
  //   // 2. We're not already updating
  //   // 3. User has appropriate role
  //   // 4. Not in order management tab
  //   if (
  //     orders.length > 0 && 
  //     !isUpdatingStatus && 
  //     userRole && 
  //     ['BOSS', 'MANAGER', 'STAFF'].includes(userRole) && 
  //     !isOrderManagementTab
  //   ) {
  //     // Add a small delay to ensure the order list is fully rendered
  //     const timer = setTimeout(() => {
  //       updateOrderStatus();
  //     }, 1000);
      
  //     return () => clearTimeout(timer);
  //   }
  // }, [orders, userRole, isOrderManagementTab]);

  const showCustomerModal = () => {
    setIsCustomerModalVisible(true);
  };

  const handleCustomerOk = async () => {
    if (!selectedCustomer) {
      message.warning('请选择客户');
      return;
    }
    try {
      await onChangeCustomer(selectedOrderIds, selectedCustomer);
      message.success('更换客户成功');
      setIsCustomerModalVisible(false);
      setSelectedCustomer(null);
    } catch (error) {
      message.error('更换客户失败');
    }
  };

  const handleCustomerCancel = () => {
    setIsCustomerModalVisible(false);
    setSelectedCustomer(null);
  };

  const updateOrderStatus = async () => {
    try {
      // Get all orders that are not delivered
      const nonDeliveredOrders = orders.filter(order => 
        order.status !== 'DELIVERED' && order.express_number
      );
      
      if (nonDeliveredOrders.length === 0) {
        message.info('没有需要更新状态的订单');
        return;
      }
      
      setIsUpdatingStatus(true);
      setTotalOrdersToUpdate(nonDeliveredOrders.length);
      setUpdatedOrders(0);
      setUpdateProgress(0);
      
      // Process orders in batches to avoid overwhelming the server
      const batchSize = 15;
      const batches = [];
      
      for (let i = 0; i < nonDeliveredOrders.length; i += batchSize) {
        batches.push(nonDeliveredOrders.slice(i, i + batchSize));
      }
      
      let processedCount = 0;
      let updatedCount = 0;
      let statusChanges = [];
      
      let batchStatusChanges = [];
      
      for (const batch of batches) {
        const batchPromises = batch.map(async (order) => {
          try {
            if (!order.express_number || !order.recipient_phone) {
              return null;
            }
            
            const response = await axios.post('/api/orders/sf_tracking/', {
              express_number: order.express_number,
              recipient_phone: order.recipient_phone
            }, { headers: getAuthHeader() });
            
            if (response.data.status && response.data.status !== order.status) {
              return {
                id: order.id,
                oldStatus: order.status,
                newStatus: response.data.status
              };
            }
            return null;
          } catch (error) {
            console.error(`Error updating order ${order.id}:`, error);
            return null;
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        const batchChanges = batchResults.filter(result => result !== null);
        
        // Add this batch's changes to the overall changes
        batchStatusChanges = [...batchStatusChanges, ...batchChanges];
        updatedCount += batchChanges.length;
        processedCount += batch.length;
        
        // Update progress after each batch
        setUpdatedOrders(processedCount);
        setUpdateProgress(Math.round((processedCount / nonDeliveredOrders.length) * 100));
        
        // Update the orders in the UI after each batch
        if (batchChanges.length > 0) {
          const updatedOrdersList = orders.map(order => {
            const change = batchChanges.find(change => change.id === order.id);
            if (change) {
              return { ...order, status: change.newStatus };
            }
            return order;
          });
          
          onSearchResults(updatedOrdersList);
        }
        
        // Add a small delay to make the progress bar animation visible
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if (batchStatusChanges.length > 0) {
        message.success(`成功更新 ${batchStatusChanges.length} 个订单状态`);
      } else {
        message.info('没有订单状态需要更新');
      }
    } catch (error) {
      console.error('Error updating order statuses:', error);
      message.error('更新订单状态失败');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleFlagUpdate = async (field, value) => {
    if (selectedOrderIds.length === 0) {
      message.warning('请选择订单');
      return;
    }

    try {
      setLoading(true);
      await batchUpdateOrderFlag(selectedOrderIds, field, value);
      message.success('更新成功');
      if (onOrdersUpdate) {
        await onOrdersUpdate();
      }
      const updatedOrders = orders.map(order => {
        if (selectedOrderIds.includes(order.id)) {
          return {
            ...order,
            [field]: value
          };
        }
        return order;
      });
      onSearchResults(updatedOrders);
    } catch (error) {
      console.error('Error updating orders:', error);
      message.error('更新失败');
    } finally {
      setLoading(false);
    }
  };

  const items = [
    {
      key: 'inspection_on',
      label: '✅  设置查验件',
      onClick: () => handleFlagUpdate('is_inspection_needed', true)
    },
    {
      key: 'inspection_off',
      label: '❎  取消查验件',
      onClick: () => handleFlagUpdate('is_inspection_needed', false)
    },
    {
      key: 'short_on',
      label: '✅  设置短装件',
      onClick: () => handleFlagUpdate('is_short', true)
    },
    {
      key: 'short_off',
      label: '❎  取消短装件',
      onClick: () => handleFlagUpdate('is_short', false)
    },
    {
      key: 'claim_on',
      label: '✅  设置理赔件',
      onClick: () => handleFlagUpdate('is_claim', true)
    },
    {
      key: 'claim_off',
      label: '❎  取消理赔件',
      onClick: () => handleFlagUpdate('is_claim', false)
    },
    {
      key: 'modified_on',
      label: '✅  设置修改件',
      onClick: () => handleFlagUpdate('is_modified', true)
    },
    {
      key: 'modified_off',
      label: '❎  取消修改件',
      onClick: () => handleFlagUpdate('is_modified', false)
    }
  ];

  // Generate recent 30 days for date filter
  const generateRecentDays = () => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      days.push({
        value: date.toISOString().split('T')[0],
        label: `${date.getMonth() + 1}月${date.getDate()}日 (${['周日', '周一', '周二', '周三', '周四', '周五', '周六'][date.getDay()]})`
      });
    }
    return days;
  };


  const isAuthorizedRole = userRole && ['BOSS', 'MANAGER', 'STAFF'].includes(userRole);

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Search inputs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Input.TextArea
              placeholder="订单号/快递单号 (每行一个)"
              id="orderSearchTextArea"
              style={{ width: 400, height: 80 }}
              allowClear
            />
            <Button 
              type="primary"
              onClick={() => {
                const textArea = document.getElementById('orderSearchTextArea');
                if (!textArea?.value) return;

                const lines = textArea.value.split('\n').filter(line => line.trim());
                if (lines.length === 0) return;

                // Send all numbers in a single search since backend handles both types
                const numbers = lines.map(line => line.trim());
                onSearchOrderDirect(numbers.join(','));
              }}
              style={{ height: 80 }}
            >
              搜索
            </Button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 160px)', gap: '8px' }}>
            <Input.Search
              placeholder="收件人"
              onSearch={value => onSearchRecipientDirect(value)}
              enterButton
              allowClear
            />
            <Input.Search
              placeholder="收件人电话"
              onSearch={value => onSearchRecipientPhoneDirect(value)}
              enterButton
              allowClear
            />
            <Input.Search
              placeholder="寄件人"
              onSearch={value => onSearchSenderDirect(value)}
              enterButton
              allowClear
            />
            <Input.Search
              placeholder="寄件人电话"
              onSearch={value => onSearchSenderPhoneDirect(value)}
              enterButton
              allowClear
            />
            <Input.Search
              placeholder="批次号"
              onSearch={value => onSearchBatchDirect(value)}
              enterButton
              allowClear
            />
            <Select
              placeholder="状态"
              style={{ width: '100%' }}
              onChange={value => onSearchStatusDirect(value)}
              allowClear
              options={[
                { value: 'CREATED', label: '已创建' },
                { value: 'PRINTED', label: '已打印' },
                { value: 'SHIPPING', label: '运输中' },
                { value: 'PENDING_CUSTOMS', label: '待清关' },
                { value: 'RELEASED', label: '已放行' },
                { value: 'DELIVERING', label: '派送中' },
                { value: 'DELIVERED', label: '已送达' }
              ]}
            />
            <Input.Search
              placeholder="打印次数"
              onSearch={value => {
                if (onSearchPrintCountDirect) {
                  onSearchPrintCountDirect(value);
                } else {
                  console.error('onSearchPrintCountDirect is not defined');
                }
              }}
              enterButton
              allowClear
            />
            <Select
              placeholder="日期筛选"
              style={{ width: '100%' }}
              mode="multiple"
              onChange={values => {
                setSelectedDates(values);
                if (onSearchDateFilterDirect) {
                  onSearchDateFilterDirect(values);
                  if (values.length > 0) {
                    message.success(`已筛选 ${values.length} 个日期`);
                  }
                } else {
                  console.error('onSearchDateFilterDirect is not defined');
                }
              }}
              allowClear
              maxTagCount="responsive"
              options={generateRecentDays()}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
          {/* {(!isOrderManagementTab || !['BOSS', 'MANAGER', 'STAFF'].includes(userRole)) && userRole !== 'CUSTOMER' && (
            <Button
              type="primary"
              icon={<InboxOutlined />}
              onClick={onBatch}
              disabled={selectedOrderIds.length === 0}
            >
              设置批次
            </Button>
          )} */}

          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={onDelete}
            disabled={selectedOrderIds.length === 0}
          >
            批量删除
          </Button>
          
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={onPrint}
            disabled={selectedOrderIds.length === 0}
          >
            批量打印
          </Button>
          <Button
            type="primary"
            icon={<ExportOutlined />}
            onClick={() => setExportModalVisible(true)}
            disabled={selectedOrderIds.length === 0 || loading}
          >
            导出订单
          </Button>
          <Button
            type="primary"
            icon={<FilePdfOutlined />}
            onClick={onDownloadPdf}
            disabled={selectedOrderIds.length === 0}
          >
            下载PDF
          </Button>
          
          {isAuthorizedRole && !isOrderManagementTab && (
            <Button
              type="primary"
              icon={<UserSwitchOutlined />}
              onClick={showCustomerModal}
              disabled={selectedOrderIds.length === 0}
            >
              更换客户
            </Button>
          )}

          {isAuthorizedRole && !isOrderManagementTab && (
            <Dropdown
              menu={{ items }}
              disabled={selectedOrderIds.length === 0 || loading}
            >
              <Button
                type="primary"
                icon={<SettingOutlined />}
              >
                设置
              </Button>
            </Dropdown>
          )}
          
          {/* 更新状态 button hidden as requested */}
          
          {isAuthorizedRole && !isOrderManagementTab && (
            <Button
              type={isFilteringUncollected ? "default" : "primary"}
              onClick={() => {
                if (isFilteringUncollected) {
                  // Cancel the filter - restore original orders
                  onSearchResults(originalOrders);
                  setIsFilteringUncollected(false);
                  message.success('已取消筛选');
                } else {
                  // Apply the filter
                  const filteredOrders = orders.filter(order => !order.trajectory_text);
                  if (filteredOrders.length === 0) {
                    message.info('没有未揽收的订单');
                    return;
                  }
                  // Save current orders before filtering
                  setOriginalOrders(orders);
                  onSearchResults(filteredOrders);
                  setIsFilteringUncollected(true);
                  message.success(`找到 ${filteredOrders.length} 个未揽收订单`);
                }
              }}
              disabled={orders.length === 0}
            >
              {isFilteringUncollected ? '取消筛选' : '筛选未揽收'}
            </Button>
          )}
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '-2px' }}>
          {(isSearchMode || isFilteringUncollected) && (
            <Button
              icon={<CloseCircleOutlined />}
              onClick={() => {
                if (isFilteringUncollected) {
                  // Reset filter state
                  setIsFilteringUncollected(false);
                  setOriginalOrders([]);
                }
                onClearSearch();
              }}
            >
              清除搜索结果
            </Button>
          )}
          {!isOrderManagementTab && (
            <>
              {userRole !== 'CUSTOMER' && (
                <Row 
                  gutter={6} 
                  style={{ 
                    background: '#f0f7ff',
                    padding: '5px',
                    margin:'10px',
                    borderRadius: '8px',
                    border: '1px solid #d6e8ff',
                    boxShadow: '0 2px 4px rgba(24,144,255,0.1)',
                    width: '60%',
                    height: '60%'
                  }}
                >
                  <StatusItem label="查验" value={orders.filter(order => order.is_inspection_needed).length} />
                  <StatusItem label="短装" value={orders.filter(order => order.is_short).length} />
                  <StatusItem label="理赔" value={orders.filter(order => order.is_claim).length} />
                  <StatusItem label="修改地址" value={orders.filter(order => order.is_modified).length} />
                </Row>
              )}
            </>
          )}
        </div>

      <Modal
        title="选择客户"
        open={isCustomerModalVisible}
        onOk={handleCustomerOk}
        onCancel={handleCustomerCancel}
        okText="确认"
        cancelText="取消"
      >
        <Select
          style={{ width: '100%' }}
          placeholder="请选择客户"
          value={selectedCustomer}
          onChange={value => setSelectedCustomer(value)}
        >
          {customers.map(customer => (
            <Select.Option key={customer.id} value={customer.id}>
              {customer.username}
            </Select.Option>
          ))}
        </Select>
      </Modal>

      <ExportFieldsModal
        visible={exportModalVisible}
        onOk={async (selectedFields) => {
          try {
            setLoading(true);
            await exportOrders(selectedOrderIds, selectedFields);
            message.success('导出成功');
          } catch (error) {
            console.error('Export error:', error);
            message.error('导出失败');
          } finally {
            setLoading(false);
            setExportModalVisible(false);
          }
        }}
        onCancel={() => setExportModalVisible(false)}
      />

    </>
  );
};

const StatusItem = ({ label, value }) => (
  <Col span={6} style={{ marginBottom: 8, padding: '6px 12px' }}>
    <Text strong style={{ marginRight: 10, color: '#1890ff', fontSize: '16px' }}>{label}：</Text>
    <Text 
      style={{ 
        padding: '3px 10px',
        background: value > 0 ? '#fff1f0' : '#f5f5f5',
        borderRadius: '4px',
        fontSize: '16px',
        fontWeight: value > 0 ? 'bold' : 'normal'
      }}
      type={value > 0 ? 'danger' : 'secondary'}
    >
      {value}
    </Text>
  </Col>
);

export default OrderListToolbar;
